// Lightsource selection. The query param is `lightsource`.
// Rules live in this file, defaultLightsourceParam.ts, loadLightsourceRows.ts, and lightsourceRow.ts.
// UI is ProductLightsources.tsx, ProductLightsource.tsx, and ProductLightsourceNoop.tsx.
// The cart payload is toCartProduct.ts. Product.tsx loads rows through loadLightsourceRows.
"use client";

import { useSyncExternalStore } from "react";

import type { CartProduct } from "./toCartProduct";

const listeners = new Set<() => void>();
let lightsourceKeys: readonly string[] = [];
let popstateBound = false;

function emit() {
  for (const listener of listeners) listener();
}

function ensurePopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    purgeUnknownLightsourceParam();
    emit();
  });
}

function subscribe(onStoreChange: () => void) {
  ensurePopstate();
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

function isListed(param: string) {
  return param === "no-lightsource" || lightsourceKeys.includes(param);
}

export function purgeUnknownLightsourceParam() {
  if (typeof window === "undefined" || lightsourceKeys.length === 0) return;
  const params = new URLSearchParams(window.location.search);
  const value = params.get("lightsource");
  if (!value || isListed(value)) return;
  params.delete("lightsource");
  const search = params.toString();
  window.history.replaceState(
    null,
    "",
    `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
  );
}

export function SyncLightsourceParam({ keys }: { keys: readonly string[] }) {
  // shortcut: key list updated during render so a stale param is gone before the checkboxes hydrate.
  lightsourceKeys = keys;
  if (typeof window !== "undefined") {
    ensurePopstate();
    purgeUnknownLightsourceParam();
  }
  return null;
}

export function lightsourceCartForParam(
  carts: readonly CartProduct[],
  param: string | null,
) {
  if (!param || param === "no-lightsource") return undefined;
  return carts.find((cart) => cart.partNo === param || cart.key === param);
}

function urlLightsourceParam() {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("lightsource");
}

export function setLightsourceParam(key: string) {
  const params = new URLSearchParams(window.location.search);
  if (params.get("lightsource") === key) return;
  params.set("lightsource", key);
  window.history.replaceState(null, "", `?${params.toString()}`);
  emit();
}

export function useLightsourceParam(serverParam: string | null) {
  return useSyncExternalStore(
    subscribe,
    () => urlLightsourceParam() ?? serverParam,
    () => serverParam,
  );
}
